import { cn } from '@/lib/cn';
import { Calculator } from 'lucide-react';
import { toNumber, toNumberOrNull } from '@/lib/number';
import { feedTypeNames } from '@/lib/feedTypes';
import type { FeedRecommendation } from '@/types/entry';

interface FeedCalculatorCardProps {
  recommendation: FeedRecommendation | null | undefined;
  isLoading?: boolean;
  className?: string;
}

export function FeedCalculatorCard({ recommendation, isLoading, className }: FeedCalculatorCardProps) {
  if (isLoading) {
    return (
      <div className={cn('bg-gray-50 rounded-xl border border-gray-200 p-5 animate-pulse', className)}>
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4" />
        <div className="h-10 bg-gray-200 rounded w-1/2" />
      </div>
    );
  }

  if (!recommendation) {
    return (
      <div className={cn('bg-gray-50 rounded-xl border border-gray-200 p-5', className)}>
        <div className="flex items-center gap-3 text-gray-500">
          <Calculator className="w-5 h-5" />
          <p className="text-sm">Feed recommendation not available</p>
        </div>
      </div>
    );
  }

  // The backend answers 0 plus a warning whenever it has nothing to recommend from.
  if (toNumber(recommendation.daily_feed_kg, 0) <= 0) {
    return (
      <div className={cn('bg-gray-50 rounded-xl border border-gray-200 p-5', className)}>
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-gray-100">
            <Calculator className="w-4 h-4 text-gray-500" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-700">Recommended Feed</h3>
            <p className="text-xs text-gray-500">
              {recommendation.calculation_method === 'doc_based' ? 'DOC-based calculation' : 'ABW-based calculation'}
            </p>
          </div>
        </div>
        <p className="mt-4 text-lg font-semibold text-gray-700">
          No recommendation yet for DOC {recommendation.doc}
        </p>
        {recommendation.warning && (
          <p className="mt-1 text-sm text-gray-500">{recommendation.warning}</p>
        )}
      </div>
    );
  }

  const feedTypeName = feedTypeNames(recommendation.feed_type);

  // Format check time from hours to readable string
  const formatCheckTime = (hours: number | string | null | undefined): string => {
    const numHours = toNumberOrNull(hours);
    if (numHours === null || numHours === undefined) return 'N/A';
    if (numHours < 1) {
      return `${Math.round(numHours * 60)} min`;
    }
    return `${numHours.toFixed(1)} hr`;
  };

  return (
    <div className={cn(
      'relative overflow-hidden rounded-xl border-2',
      'bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50',
      'border-emerald-200',
      className
    )}>
      {/* Decorative pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="feed-pattern" width="24" height="24" patternUnits="userSpaceOnUse">
              <path d="M12 0L24 12L12 24L0 12Z" fill="none" stroke="currentColor" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#feed-pattern)" />
        </svg>
      </div>

      <div className="relative p-5">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 rounded-lg bg-emerald-100">
            <Calculator className="w-4 h-4 text-emerald-600" />
          </div>
          <div>
            <h3 className="font-semibold text-emerald-900">Recommended Feed</h3>
            <p className="text-xs text-emerald-600">
              {recommendation.calculation_method === 'doc_based' ? 'DOC-based calculation' : 'ABW-based calculation'}
            </p>
          </div>
        </div>

        {/* Main recommendation */}
        <div className="flex items-end gap-4 mb-4">
          <div>
            <p className="text-4xl font-bold text-emerald-700 tabular-nums">
              {Number(recommendation.daily_feed_kg).toFixed(1)}
            </p>
            <p className="text-sm text-emerald-600">kg / day</p>
          </div>
        </div>

        {/* Warning message if present */}
        {recommendation.warning && (
          <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-sm text-amber-800">
              {recommendation.warning}
            </p>
          </div>
        )}

        {/* Details grid */}
        <div className="grid grid-cols-3 gap-3 pt-4 border-t border-emerald-200/50">
          <div className="text-center">
            <p className="text-xs text-emerald-600">Feed Type</p>
            <p className="font-semibold text-emerald-900">{recommendation.feed_type}</p>
            {feedTypeName && <p className="text-xs text-emerald-700">{feedTypeName}</p>}
          </div>
          {recommendation.tray_g_per_kg !== null && recommendation.tray_g_per_kg !== undefined && (
            <div className="text-center">
              <p className="text-xs text-emerald-600">Tray Amount</p>
              <p className="font-semibold text-emerald-900">{recommendation.tray_g_per_kg}g/kg</p>
            </div>
          )}
          {recommendation.check_time_hours !== null && recommendation.check_time_hours !== undefined && (
            <div className="text-center">
              <p className="text-xs text-emerald-600">Check Time</p>
              <p className="font-semibold text-emerald-900">{formatCheckTime(recommendation.check_time_hours)}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
